import { Upload } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import type { Verdict } from './prima-nota-derived';

const VERDICT_TONES: Record<Verdict, 'error' | 'warning' | 'success'> = {
  blocked: 'error',
  warnings: 'warning',
  ready: 'success',
};

/** The month's state in one word, in the semantic colours of the Brand Kit. */
export function VerdictBadge({ verdict }: { verdict: Verdict }) {
  const { t } = useTranslation('finance');
  return (
    <span className={`status-badge ${VERDICT_TONES[verdict]}`}>
      {t(`primaNota.status.${verdict}`)}
    </span>
  );
}

/**
 * One block of the page: a card with its heading row and its body. Every step is one, so the page
 * reads as a set of tasks rather than as one long form. `tone="error"` marks the one block that
 * stops everything else: the missing profile.
 */
export function Block({
  id,
  title,
  aside,
  tone,
  testId,
  focusable = false,
  children,
}: {
  id: string;
  title: string;
  aside?: ReactNode;
  tone?: 'error';
  testId?: string;
  /** Whether its heading can take the focus from a link that points at the block. */
  focusable?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      aria-labelledby={id}
      className={
        tone === 'error'
          ? 'border-destructive/40 bg-card rounded-lg border'
          : 'border-border bg-card rounded-lg border'
      }
      {...(testId === undefined ? {} : { 'data-testid': testId })}
    >
      <div className="border-border flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b px-4 py-3">
        <h3 className="card-title" id={id} {...(focusable ? { tabIndex: -1 } : {})}>
          {title}
        </h3>
        {aside}
      </div>
      <div className="space-y-4 p-4">{children}</div>
    </section>
  );
}

/** One labelled text box. The declaration editor needs five of them per row. */
export function LabelledInput(props: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <label className="field-label" htmlFor={props.id}>
        {props.label}
      </label>
      <Input
        id={props.id}
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
      />
    </div>
  );
}

/**
 * A file the month needs, with a button to choose it. The browser's own file input is kept — it is
 * what assistive technology and the tests drive — but visually hidden behind a button in the
 * console's language: the native control spoke the browser's ("Choose File · No file chosen").
 * The focus ring is an outline on the label, shown while the hidden input has focus: a box-shadow
 * ring would vanish in forced-colours mode.
 */
export function FilePicker(props: {
  label: string;
  hint: string;
  choose: string;
  busy: boolean;
  /** Another action is in flight: the picker may not start one of its own. */
  locked: boolean;
  busyLabel: string;
  testId: string;
  onPick: (file: File) => void;
}) {
  return (
    <div className="border-chip-border bg-background flex flex-wrap items-center justify-between gap-3 rounded-md border border-dashed px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{props.label}</p>
        <p className="text-muted-foreground text-[13px] leading-5">{props.hint}</p>
      </div>
      <label
        // 44 px on a phone, where a finger is the pointer (the standard's 44×44); the shell is the
        // container the phone layout is measured on, as in shell.css.
        className={`border-chip-border bg-card text-foreground focus-within:outline-ring inline-flex min-h-[38px] items-center gap-2 rounded-md border px-3 text-sm font-semibold focus-within:outline-2 focus-within:outline-offset-2 @max-[620px]/shell:min-h-[44px] ${
          props.locked ? 'cursor-default opacity-50' : 'hover:bg-accent cursor-pointer'
        }`}
      >
        <Upload aria-hidden="true" className="size-4" />
        {props.busy ? props.busyLabel : props.choose}
        <input
          type="file"
          data-testid={props.testId}
          disabled={props.locked}
          // Named after the file it takes: a screen reader listing the controls would otherwise
          // hear «Scegli il file» once per picker and nothing to tell them apart.
          aria-label={`${props.choose}: ${props.label}`}
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file !== undefined) {
              props.onPick(file);
            }
            // Clearing lets the same file be chosen again after a failed upload, which otherwise
            // fires no change event and looks like the button is dead.
            event.target.value = '';
          }}
        />
      </label>
    </div>
  );
}
